import React, {
  Fragment,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { ColDef, SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { RootState } from 'data/root.reducers';
import { Filters, FiltersPrivileges } from 'data/filters/filters.types';
import { AuditDetails, GridSettings, WithAuditDetails } from 'data/types';
import * as selectors from 'data/filters/filters.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/filters/filters.actions';
import { AppToaster } from 'shared/toaster';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import cloneDeep from 'lodash.clonedeep';
import FiltersGrid from '../../components/FiltersGrid';
import { filtersColumnDefs } from '../../Filters.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  privileges: authenticationSelectors.getAuthenticationPrivileges(state),
  beforeLogout: authenticationSelectors.getAuthenticationBeforeLogout(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  data: selectors.getFiltersList(state),
  selectedFilters: selectors.getSelectedFilters(state),
});

const mapDispatch = {
  goToFiltersCreate: actions.goToFiltersCreate,
  goToFiltersEdit: actions.goToFiltersEdit,
  goToFiltersView: actions.goToFiltersView,
  selectFilters: actions.selectFilters,
  getFiltersList: actions.getFiltersList,
  deleteFilters: actions.deleteFilters,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const FiltersList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  privileges,
  deliveryFunctionId,
  data = [],
  beforeLogout,
  selectedFilters,
  history,
  goToFiltersCreate,
  goToFiltersEdit,
  selectFilters,
  deleteFilters,
  goToFiltersView,
  getFiltersList,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [dataGrid, setDataGrid] = useState<Filters[] | []>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>(filtersColumnDefs);

  useEffect(() => {
    getFiltersList();
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getFiltersList]);

  useEffect(() => {
    if (data) {
      const clonedData = cloneDeep(data);
      setDataGrid(clonedData);
    }
  }, [data]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getFiltersList();

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToFiltersCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectFilters(event.api.getSelectedRows());
  };

  const handleShowActionMenuItem = () => true;

  const handleEdit = () => goToFiltersEdit(history);

  const handleSelectItemMenuAction = () => goToFiltersView(history);

  const handleDelete = () => {
    const filterId = selectedFilters[0]?.id;
    if (filterId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedFilters[0]?.id,
        AuditService.FILTERS,
        AuditActionType.FILTERS_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteFilters(filterId, body);
  };

  const hasPrivilegeToView = privileges.includes(FiltersPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(FiltersPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(FiltersPrivileges.Delete);

  return (
    <Fragment>
      <FiltersGrid
        data={dataGrid}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={filtersColumnDefs}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onSelectItemMenuAction={handleSelectItemMenuAction}
        onShowActionMenuItem={handleShowActionMenuItem}
        lastUpdatedAt={lastUpdatedAt}
        selectedFilters={selectedFilters?.[0]}
        onDeleteRow={handleDelete}
        onEdit={handleEdit}
      />
    </Fragment>
  );
};

export default withRouter(connector(FiltersList));
